import { CalendarFragmentKey, CalendarSurface, FragmentConfig, LayoutZone, SurfaceLayout, zoneItems, zonesForSurface } from '../model';
import { CalendarFragmentDefinition } from '../fragments/types';
import { fragmentsForSurface } from '../fragments/registry';

/**
 * Pure operations on a surface layout. Kept free of React so the editor's reducer-ish flow stays
 * testable and every mutation produces a fresh object rather than editing in place.
 *
 * Invariant upheld throughout: a fragment appears at most once per surface. Every insert therefore
 * removes the fragment from wherever it currently sits first, which makes "move between zones" and
 * "add from the palette" the same operation.
 */

export interface Placement {
  zone: LayoutZone;
  index: number;
}

export const findPlacement = (layout: SurfaceLayout, key: CalendarFragmentKey): Placement | null => {
  for (const zone of Object.keys(layout) as LayoutZone[]) {
    const index = zoneItems(layout, zone).findIndex(item => item.key === key);
    if (index >= 0) return { zone, index };
  }
  return null;
};

export const placedKeys = (layout: SurfaceLayout): CalendarFragmentKey[] =>
  (Object.keys(layout) as LayoutZone[]).flatMap(zone => zoneItems(layout, zone).map(item => item.key));

export const unplacedFragments = (layout: SurfaceLayout, surface: CalendarSurface): CalendarFragmentDefinition[] => {
  const placed = new Set<string>(placedKeys(layout));
  return fragmentsForSurface(surface).filter(definition => !placed.has(definition.key));
};

export const removeFragment = (layout: SurfaceLayout, key: CalendarFragmentKey): SurfaceLayout => {
  const next: SurfaceLayout = {};
  (Object.keys(layout) as LayoutZone[]).forEach(zone => {
    next[zone] = zoneItems(layout, zone).filter(item => item.key !== key);
  });
  return next;
};

/**
 * Inserts (or moves) a fragment into a zone.
 *
 * `index` is interpreted against the zone *after* the fragment has been removed from its old spot,
 * so dragging an item downwards within one zone lands where the drop indicator was shown.
 */
export const insertFragment = (
  layout: SurfaceLayout,
  zone: LayoutZone,
  key: CalendarFragmentKey,
  index?: number,
  options?: Partial<Omit<FragmentConfig, 'key'>>,
): SurfaceLayout => {
  const existing = findPlacement(layout, key);
  const existingConfig = existing ? zoneItems(layout, existing.zone)[existing.index] : undefined;
  const cleaned = removeFragment(layout, key);
  const target = [...zoneItems(cleaned, zone)];
  // The caller's index was measured against the zone *before* the fragment was pulled out of it, so
  // dragging an item downwards within its own zone would otherwise land one slot too far.
  const shift = existing && existing.zone === zone && index != null && index > existing.index ? 1 : 0;
  const requested = index == null ? target.length : index - shift;
  const at = requested < 0 || requested > target.length ? target.length : requested;
  target.splice(at, 0, { ...(existingConfig ?? { key }), ...options, key });
  return { ...cleaned, [zone]: target };
};

/** Reorders within the fragment's own zone; a no-op at either end. */
export const moveFragment = (layout: SurfaceLayout, key: CalendarFragmentKey, direction: -1 | 1): SurfaceLayout => {
  const placement = findPlacement(layout, key);
  if (!placement) return layout;
  const items = [...zoneItems(layout, placement.zone)];
  const target = placement.index + direction;
  if (target < 0 || target >= items.length) return layout;
  const [moved] = items.splice(placement.index, 1);
  items.splice(target, 0, moved);
  return { ...layout, [placement.zone]: items };
};

export const updateFragment = (layout: SurfaceLayout, key: CalendarFragmentKey, patch: Partial<Omit<FragmentConfig, 'key'>>): SurfaceLayout => {
  const placement = findPlacement(layout, key);
  if (!placement) return layout;
  const items = zoneItems(layout, placement.zone).map(item => (item.key === key ? { ...item, ...patch } : item));
  return { ...layout, [placement.zone]: items };
};

/** Guarantees every zone the surface renders exists as an array, so the editor can map over them. */
export const normalizeSurface = (layout: SurfaceLayout, surface: CalendarSurface): SurfaceLayout => {
  const next: SurfaceLayout = {};
  zonesForSurface(surface).forEach(zone => {
    next[zone] = zoneItems(layout, zone);
  });
  return next;
};

/**
 * Rough rendered height of a card, in pixels, used only to warn that a configuration will clip in
 * short slots. Deliberately an estimate: the real height depends on data, wrapping and font
 * metrics, so the designer warns rather than forbids.
 */
export const estimateCardHeight = (layout: SurfaceLayout): number => {
  const CARD_PADDING = 16;
  const HEADER_ROW = 22;
  const BODY_ROW = 14;
  const LABEL_ROW = 4;
  // `gap-y-1.5` between the body's fragments — see CalendarEventCard. It falls between rows, so a
  // body of n fragments carries n-1 of them.
  const BODY_GAP = 6;
  const hasHeader = zoneItems(layout, LayoutZone.HeaderLeft).length > 0 || zoneItems(layout, LayoutZone.HeaderRight).length > 0;
  const bodyItems = zoneItems(layout, LayoutZone.Body);
  const rows = bodyItems.reduce((height, item) => height + (item.key === CalendarFragmentKey.Labels ? LABEL_ROW : BODY_ROW), 0);
  const gaps = Math.max(bodyItems.length - 1, 0) * BODY_GAP;
  return CARD_PADDING + (hasHeader ? HEADER_ROW : 0) + rows + gaps;
};

/**
 * Real geometry of a timeslot calendar event, so the preview clips where the calendar clips.
 *
 * The calendar runs FullCalendar at its default 30-minute `slotDuration` (see the `slotsPerHour = 2`
 * assumption in TimeslotCalendar's scroll maths), and the slot cell height is pinned in CSS:
 * `.calendar-condensed .fc-timegrid-slot { h-[54px] }` / `.calendar-extended { h-[150px] }`
 * (styles/ringil/theme.css). The card is inset by `.fc .fc-timegrid-event { p-1 }` top and bottom.
 */
export const SLOT_MINUTES = 30;

export type CalendarDensity = 'condensed' | 'extended';

export const SLOT_CELL_HEIGHT: Record<CalendarDensity, number> = {
  condensed: 54,
  extended: 150,
};

/** Top + bottom padding FullCalendar puts around the event content. */
const EVENT_INSET = 8;

export const cardHeightForMinutes = (minutes: number, density: CalendarDensity): number =>
  Math.max(0, Math.round((minutes / SLOT_MINUTES) * SLOT_CELL_HEIGHT[density]) - EVENT_INSET);
